import { useT } from "@transifex/react";
import { useRef, useState } from "react";
import PhaseSixIcon from "../../basic/phaseSixIcon/PhaseSixIcon";
import Avatar from "./Avatar";
import { buildAccountLinks } from "../../utils/userUtils";

interface HeaderBurgerMenuAccountProps {
    avatarUrl?: string;
    firstName: string;
    lastName: string;
    currentPath: string;
    role: string;
    accountTranslations: {
        myAccount: string;
        myParentAccount: string;
        myLearningAccount: string;
    };
}

const HeaderBurgerMenuAccount = (props: HeaderBurgerMenuAccountProps) => {
    const t = useT();
    const t_userAvatar = t("User avatar", {
        _tags: "TeacherArea, Account, ScreenReaderOnly",
    });
    // Plural on purpose — "My Account" is already the first dropdown link right below the row.
    const t_myAccounts = t("My accounts", { _tags: "TeacherArea, Account" });

    // Teachers registered via SSO may have no name at all; never render an empty account row.
    const displayName = `${props.firstName} ${props.lastName}`.trim() || t_myAccounts;

    const accountLinks = buildAccountLinks({
        translations: props.accountTranslations,
        userRole: props.role,
    });

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const burgerAccountToggleId = "burger-account-toggle";

    const dropdownRef = useRef<HTMLDivElement>(null);

    const dropdownAriaControlId = "burger-account-dropdown";

    const toggleInnerAccountDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    return (
        <div
            className="divide-stone-400 flex w-full flex-col divide-y overflow-visible"
            id="header-login-container"
        >
            <button
                className="no-close-burgerdropdown text-stone-1000 p-s gap-s flex cursor-pointer flex-row items-center"
                role="menuitem"
                id={burgerAccountToggleId}
                aria-expanded={isDropdownOpen}
                aria-haspopup="true"
                aria-controls={dropdownAriaControlId}
                onClick={toggleInnerAccountDropdown}
            >
                <Avatar
                    img={{
                        src: props.avatarUrl ?? "",
                        alt: t_userAvatar,
                    }}
                    borderColor="border-stone-800"
                    backgroundColor="bg-white"
                    sizeClasses="max-h-xl min-h-xl max-w-xl min-w-xl"
                />
                <p className="text-header-l flex flex-1 text-left">{displayName}</p>
                <span id="chevron-icon">
                    <PhaseSixIcon
                        className="text-xl"
                        name={isDropdownOpen ? "chevron-up" : "chevron-down"}
                    />
                </span>
            </button>

            {isDropdownOpen && (
                <div
                    ref={dropdownRef}
                    className="border-stone-400 flex flex-col border-t border-b"
                    id={dropdownAriaControlId}
                    aria-labelledby={burgerAccountToggleId}
                    role="menu"
                >
                    {accountLinks.map((element, index) => (
                        <a
                            key={element.link}
                            className="pl-l pr-m flex w-full flex-col items-center"
                            href={element.link}
                            id={`${element.text}-headerlink`}
                            role="menuitem"
                            onClick={() => setIsDropdownOpen(false)}
                        >
                            <div
                                className={`text-stone-1000 text-l py-s dropdown-link group px-xs flex w-full flex-row items-center justify-start ${
                                    index < accountLinks.length - 1 ? "border-stone-400 border-b" : ""
                                }`}
                            >
                                <p
                                    className={`header-nav-link ${
                                        element.link === props.currentPath ? "header-nav-link-active" : ""
                                    }`}
                                    data-link-path={element.link}
                                >
                                    {element.text}
                                </p>
                            </div>
                        </a>
                    ))}
                </div>
            )}
        </div>
    );
};

export default HeaderBurgerMenuAccount;
